import Router from 'next/router';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import config, { Country, Language, Locale } from '../config';
import UserService from '../services/user.service';
import store, { cache } from '../store';
import { selectUser } from '../store/selectors/user';
import {
  getAvailableLanguagesForCountry,
  getDefaultLanguageForCountry,
  isLanguageAvailableForCountry,
} from '../utils/country-language-mapping';
import i18n, { getLocale, setLocale } from '../utils/i18n';

type SetLanguageByLocaleArgs = {
  locale: Locale;
  saveToDb?: boolean;
};

type SetLanguageByLanguageArgs = {
  language: Language;
  country: Country;
  saveToDb?: boolean;
};

type SetLanguageArgs = SetLanguageByLocaleArgs | SetLanguageByLanguageArgs;

const userService = new UserService();

export const setLanguage = async (args: SetLanguageArgs) => {
  const locale =
    (args as SetLanguageByLocaleArgs)?.locale ||
    `${(args as SetLanguageByLanguageArgs).language}-${(args as SetLanguageByLanguageArgs).country}`;

  const saveToDb = args.saveToDb;

  const language = (args as SetLanguageByLanguageArgs).language || ((args as SetLanguageByLocaleArgs)?.locale?.split?.('-')[0] as Language);

  const isLoggedIn = store.getState()?.userData?.user?.isLoggedIn;

  // We can only save language when user is logged in.
  if (saveToDb && isLoggedIn) {
    await userService.updateUserLanguage(language);
  }

  await Promise.all([
    i18n.changeLanguage(locale), //
  ]);

  // Update locale.
  setLocale(locale);

  // Clear cached data.
  cache.clear();

  // Soft-reload.
  Router.replace(Router.asPath);

  // TODO: Use actual reload if we have other requests that we need to reset in redux or similar. Currently soft-reload seems to be sufficient.
  // window.location.reload();
};

export const useLanguage = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);

  const storedLocale = getLocale();
  const [storedLanguage, storedCountry] = storedLocale?.split?.('-') ?? [];

  // Use user's current country for dynamic language availability
  // Fall back to deployment country during SSR or when user data is loading
  const commonDataLocale = store.getState()?.commonData?.locale;
  const deploymentCountry = (commonDataLocale?.split('-')[1] as Country) || config.get('country');
  const currentCountry = (user?.country as Country) || (storedCountry as Country) || deploymentCountry;

  let availableLanguages: Language[];

  try {
    availableLanguages = getAvailableLanguagesForCountry(currentCountry);
  } catch (error) {
    console.error('Error getting available languages for country:', currentCountry, error);
    availableLanguages = [getDefaultLanguageForCountry(currentCountry)];
  }

  const setLanguageInner = async (args: SetLanguageArgs) => {
    await setLanguage(args);
  };

  const getLanguageWithTranslationLabel = (language: Language) =>
    `${t(`languages.${language}.name`)} (${t(`languages.${language}.translation`)})`;

  const getLanguageShortName = (language: Language) => {
    const shortName = t(`languages.${language}.shortName`);

    return shortName || language;
  };

  const languageName = t(`languages.${storedLanguage}.name`);
  const languageNameWithTranslation = getLanguageWithTranslationLabel(storedLanguage as Language);

  return {
    language: storedLanguage as Language,
    locale: storedLocale as Locale,
    country: storedCountry as Country,
    currentUserCountry: currentCountry,
    availableLanguages,
    setLanguage: setLanguageInner,
    languageName,
    languageNameWithTranslation,
    getLanguageWithTranslationLabel,
    getLanguageShortName,
    isCurrentLanguageAvailableForCountry: (country: Country) => isLanguageAvailableForCountry(storedLanguage as Language, country),
  };
};

export { getLanguage, getLocale } from '../utils/i18n';
